import React from "react";

import { useGetApplicationQuery, useUpdateIndividualApplicationMutation } from "IndividualApplication/api";
import { IndividualApplication } from "IndividualApplication/types";
import { enterInformationURL, individualDentalPlanURL, individualVisionPlanURL } from "IndividualApplication/urls";
import { individualEligibleDental, individualEligibleVision, individualURLHelper, sourceLocation } from "IndividualApplication/utils";
import { capitalize } from "lodash";
import { Link, useHistory } from "react-router-dom";

import Button, { LinkButton } from "Cobalt/Button";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";

import type { StyleProps } from "Utils/styleProps";

import makeBem from "Utils/makeBem";

const bem = makeBem("IndividualNavSection");

const backURL = (individualApplication: IndividualApplication, page: string) => {
  if (page == "vision" && individualEligibleDental(individualApplication)) {
    return individualURLHelper(individualDentalPlanURL);
  }
  if (page == "enterInformation") {
    if (individualEligibleVision(individualApplication)) {
      return individualURLHelper(individualVisionPlanURL);
    }
    return individualURLHelper(individualDentalPlanURL);
  }
  return "";
};

const nextURL = (individualApplication: IndividualApplication, page: string) => {
  if (page == "vision" || (page == "dental" && !individualEligibleVision(individualApplication))) {
    return individualURLHelper(enterInformationURL);
  }
  if (page == "dental" && individualEligibleVision(individualApplication)) {
    return individualURLHelper(individualVisionPlanURL);
  }
};

export interface IndividualNavSectionProps extends StyleProps {
  page: string;
  submitButton?: JSX.Element;
}

export const IndividualNavSection = ({ page, submitButton }: IndividualNavSectionProps) => {
  const history = useHistory();
  const { data: individualApplication } = useGetApplicationQuery();
  const [updateIndividualApplication] = useUpdateIndividualApplicationMutation();

  const backToSource = () => {
    if (page === "dental" || (page === "vision" && !individualEligibleDental(individualApplication))) {
      const sourceLocationData = sourceLocation();
      if (sourceLocationData.source === "ee-application") {
        window.location.pathname = sourceLocationData.source_url;
      } else {
        history.push("/employee-dashboard");
      }
    } else {
      history.push(backURL(individualApplication, page));
    }
  };

  const resetIndividualCart = async () => {
    let exitApplication = false;

    if (page === "dental") {
      await updateIndividualApplication({
        individualDentalPlanID: "",
        dependentEnrollmentDental: "",
      });
      // If user is ineligible for vision, exit application
      if (!individualEligibleVision(individualApplication)) {
        exitApplication = true;
      }
    } else if (page === "vision") {
      await updateIndividualApplication({
        individualVisionPlanID: "",
        dependentEnrollmentVision: "",
      });
      // If user is ineligible for dental, exit application
      if (!individualEligibleDental(individualApplication) || !individualApplication?.individualDentalPlanID) {
        exitApplication = true;
      }
    }
    // Exit application if both plans are ineligible or not selected
    if (exitApplication) {
      history.push("/employee-dashboard");
    } else if (page === "dental" && individualEligibleVision(individualApplication)) {
      history.push(individualURLHelper(individualVisionPlanURL));
    } else if (page === "vision" || (page === "dental" && !individualEligibleVision(individualApplication))) {
      history.push(individualURLHelper(enterInformationURL));
    }
  };

  return (
    <Div className={bem()}>
      <Div className={bem("back")}>
        <Button onClick={backToSource} pl={0} color="tertiary">
          <Icon icon="chevron-left" pr={3} />
          Back
        </Button>
      </Div>
      <Div className={bem("next")}>
        {submitButton || (
          <Button color="secondary" onClick={resetIndividualCart}>
            Continue Without {capitalize(page)}
          </Button>
        )}
      </Div>
    </Div>
  );
};
